/**
 * Global Table Configuration
 * This configuration can be loaded from a database in the future
 * Structure is designed to be JSON-serializable
 */

export interface TableConfiguration {
	columns: ColumnsConfig;
	rows: RowsConfig;
	typography: TableTypographyConfig;
	borders: BordersConfig;
	colors: TableColorsConfig;
	alignment: AlignmentConfig;
	freezing: FreezingConfig;
	spacing: TableSpacingConfig;
	statMarkers: IStatMarkerConfig;
	visibleColumns: VisibleColumnsConfig;
	// Add to TableConfiguration interface, after visibleColumns:
	yAxisWidth: ITableYAxisWidthConfig;
}

interface VisibleColumnsConfig {
	target: number; // Target number of visible numerical columns after frozen columns
	fallbacktarget1: number; //when the number of numerical columns less than Target, then while calculating the dynamic column width this is the fallback tagret 1
	fallbacktarget2: number; //when the number of numerical columns less than Target, then while calculating the dynamic column width this is the fallback tagret 2
}
// Add new interface after VisibleColumnsConfig
export interface ITableYAxisWidthConfig {
	/** Absolute floor — never go below this */
	floorPx: number;
	/** Max fraction of container width the attribute column may consume */
	maxContainerFraction: number;
	/** Padding added to measured text (cell padding + safety) */
	paddingPx: number;
	/** Minimum content-driven width */
	minContentPx: number;
	/** Hard ceiling in px */
	ceilingPx: number;
}

// Multi-tile variant
export interface ITableYAxisWidthConfigMultiTile {
	floorPx: number;
	maxContainerFraction: number;
	paddingPx: number;
	minContentPx: number;
	ceilingPx: number;
}

interface ColumnsConfig {
	attribute: {
		minWidth: number;
		maxWidth: number;
		minWidthColumnCharts: number; // For column-type charts
		alignment: "left" | "center" | "right";
		wrapText: boolean;
		maxLines: number;
		truncate: boolean;
	};
	wave: {
		minWidth: number;
		maxWidth: number;
		alignment: "left" | "center" | "right";
		wrapText: boolean;
		maxLines: number;
		truncate: boolean;
		widthCalculation: "longestLabel" | "fixed";
	};
	numerical: {
		minWidth: number;
		maxWidth: number;
		alignment: "left" | "center" | "right";
		wrapText: boolean;
		maxLines: number;
		truncate: boolean;
		consistentWidth: boolean;
		widthCalculation: "lowestHeaderLevel" | "fixed";
	};
}

interface RowsConfig {
	height: {
		auto: boolean;
		minHeight: number;
		maxHeight: number;
		consistent: boolean;
	};
	content: {
		wrapText: boolean;
		maxLines: number;
		truncate: boolean;
	};
}

interface TableTypographyConfig {
	fontFamily: string;
	fontSize: {
		base: number;
		header: number;
		data: number;
	};
	fontWeight: {
		header: number;
		data: number;
		attribute: number;
	};
	lineHeight: number;
	color: {
		default: string;
		header: string;
		attribute: string;
		lowNSize: string;
		tertiary: string;
	};
	charWidth: number;
	headerHierarchy: {
		level0: {
			fontSize: number;
			fontWeight: number;
			color: string;
			lineHeight: string | number;
		};
		level1: {
			fontSize: number;
			fontWeight: number;
			color: string;
			lineHeight: string | number;
		};
		level2: {
			fontSize: number;
			fontWeight: number;
			color: string;
			lineHeight: string | number;
		};
		leafLevel: {
			fontSize: number;
			fontWeight: number;
			color: string;
			lineHeight: string | number;
		};
		nSizeRow: {
			fontSize: number;
			fontWeight: number;
			color: string;
			lineHeight: string | number;
		};
		default: {
			fontSize: number;
			fontWeight: number;
			color: string;
			lineHeight: string | number;
		};
	};
}

interface BordersConfig {
	width: {
		bold: number;
		normal: number;
		grey: number;
	};
	color: {
		bold: string;
		normal: string;
		grey: string;
	};
	style: {
		solid: string;
		dashed: string;
	};
	application: {
		outerBorder: "bold" | "normal" | "grey";
		innerBorder: "bold" | "normal" | "grey";
		headerBorder: "bold" | "normal" | "grey";
		hierarchyBorder: "bold" | "normal" | "grey";
	};
}

interface TableColorsConfig {
	background: {
		white: string;
		evenColumn: string;
		oddColumn: string;
		header: string;
		fixedColumn: string;
		fixedRow: string;
	};
	banding: {
		enabled: boolean;
		pattern: "column" | "row";
		evenColor: string;
		oddColor: string;
	};
}

interface AlignmentConfig {
	horizontal: {
		default: "left" | "center" | "right";
		attribute: "left" | "center" | "right";
		wave: "left" | "center" | "right";
		numerical: "left" | "center" | "right";
		header: "left" | "center" | "right";
	};
	vertical: {
		default: "top" | "middle" | "bottom";
		header: "top" | "middle" | "bottom";
		data: "top" | "middle" | "bottom";
	};
}

interface FreezingConfig {
	columns: {
		enabled: boolean;
		freezeLeft: string[];
		matchChartWidth: boolean;
	};
	rows: {
		enabled: boolean;
		freezeTop: string[];
		matchChartHeight: boolean;
	};
}

interface TableSpacingConfig {
	padding: {
		cell: {
			horizontal: number;
			vertical: number;
		};
		header: {
			horizontal: number;
			vertical: number;
		};
	};
}

export enum StatMarkerPositionType {
	Left = "left",
	Right = "right",
	Top = "top",
	Bottom = "bottom",
}

export interface IStatMarkerConfig {
	enabled: boolean;
	position: StatMarkerPositionType;
	fontSize: number;
	fontWeight: number;
	color: string;
	spacing: number;
	maxLines: number;
	maxWidth: string;
}
export const TABLE_ATTR_SINGLE_DEFAULTS: ITableYAxisWidthConfig = {
	floorPx: 120,
	maxContainerFraction: 0.3,
	paddingPx: 32, // 16px padding each side
	minContentPx: 120,
	ceilingPx: 220,
};

export const TABLE_ATTR_MULTI_TILE_DEFAULTS: ITableYAxisWidthConfig = {
	floorPx: 60,
	maxContainerFraction: 0.28,
	paddingPx: 16,
	minContentPx: 60,
	ceilingPx: 140,
};
/**
 * Default table configuration
 */
export const defaultTableConfig: TableConfiguration = {
	columns: {
		attribute: {
			minWidth: 120,
			maxWidth: 220,
			minWidthColumnCharts: 180, // Larger width for column-type charts
			alignment: "right",
			wrapText: true,
			maxLines: 4,
			truncate: true,
		},
		wave: {
			minWidth: 44,
			maxWidth: 200,
			alignment: "center",
			wrapText: true,
			maxLines: 4,
			truncate: true,
			widthCalculation: "longestLabel",
		},
		numerical: {
			minWidth: 62,
			maxWidth: 200,
			alignment: "center",
			wrapText: true,
			maxLines: 4,
			truncate: true,
			consistentWidth: true,
			widthCalculation: "lowestHeaderLevel",
		},
	},
	rows: {
		height: {
			auto: true,
			minHeight: 32,
			maxHeight: 120,
			consistent: true,
		},
		content: {
			wrapText: true,
			maxLines: 4,
			truncate: true,
		},
	},
	typography: {
		fontFamily: "inherit",
		fontSize: {
			base: 11,
			header: 11,
			data: 11,
		},
		fontWeight: {
			header: 400,
			data: 400,
			attribute: 400,
		},
		lineHeight: 1.4,
		color: {
			default: "#403C45",
			header: "#403C45",
			attribute: "#403c45",
			lowNSize: "#B70000",
			tertiary: "#2F4B7F",
		},
		charWidth: 7,
		headerHierarchy: {
			level0: {
				fontSize: 12,
				fontWeight: 400,
				color: "#5E80B7",
				lineHeight: 16,
			},
			level1: {
				fontSize: 11,
				fontWeight: 400,
				color: "#2F4B7F",
				lineHeight: 16,
			},
			level2: {
				fontSize: 11,
				fontWeight: 400,
				color: "#2F4B7F",
				lineHeight: 16,
			},
			leafLevel: {
				fontSize: 11,
				fontWeight: 400,
				color: "#2F4B7F",
				lineHeight: 16,
			},
			nSizeRow: {
				fontSize: 11, // Same as leafLevel
				fontWeight: 400, //
				color: "#2F4B7F",
				lineHeight: 14,
			},
			default: {
				fontSize: 11,
				fontWeight: 400,
				color: "#63646B",
				lineHeight: 16,
			},
		},
	},
	borders: {
		width: {
			bold: 2,
			normal: 1,
			grey: 1,
		},
		color: {
			bold: "#D6DFED",
			normal: "#D6DFED",
			grey: "#E8EDF5",
		},
		style: {
			solid: "solid",
			dashed: "dashed",
		},
		application: {
			outerBorder: "normal",
			innerBorder: "normal",
			headerBorder: "bold",
			hierarchyBorder: "grey",
		},
	},
	colors: {
		background: {
			white: "#ffffff",
			evenColumn: "transparent",
			oddColumn: "rgb(248, 250, 252)",
			header: "#ffffff",
			fixedColumn: "#ffffff",
			fixedRow: "#ffffff",
		},
		banding: {
			enabled: true,
			pattern: "column",
			evenColor: "transparent",
			oddColor: "#f5f5f5",
		},
	},
	alignment: {
		horizontal: {
			default: "center",
			attribute: "right",
			wave: "center",
			numerical: "center",
			header: "center",
		},
		vertical: {
			default: "middle",
			header: "middle",
			data: "middle",
		},
	},
	freezing: {
		columns: {
			enabled: true,
			freezeLeft: ["attribute"],
			matchChartWidth: true,
		},
		rows: {
			enabled: true,
			freezeTop: ["hierarchy", "attribute", "nSize"],
			matchChartHeight: true,
		},
	},
	spacing: {
		padding: {
			cell: {
				horizontal: 0,
				vertical: 0,
			},
			header: {
				horizontal: 0,
				vertical: 0,
			},
		},
	},
	visibleColumns: {
		target: 13,
		fallbacktarget1: 8, //first fall back
		fallbacktarget2: 12, //seconf fall back
	},
	statMarkers: {
		enabled: true,
		position: StatMarkerPositionType.Top,
		fontSize: 7,
		fontWeight: 400,
		color: "#FF0000",
		spacing: 0,
		maxLines: 4,
		maxWidth: "100%",
	},
	yAxisWidth: TABLE_ATTR_SINGLE_DEFAULTS,
};

/**
 * Configuration manager class for tables
 */
export class TableConfigManager {
	private static instance: TableConfigManager;
	private config: TableConfiguration;

	private constructor(config?: TableConfiguration) {
		this.config = config || defaultTableConfig;
	}

	static getInstance(config?: TableConfiguration): TableConfigManager {
		if (!TableConfigManager.instance) {
			TableConfigManager.instance = new TableConfigManager(config);
		}
		return TableConfigManager.instance;
	}

	async loadFromDatabase(configId?: string): Promise<void> {
		try {
			this.config = defaultTableConfig;
		} catch (error) {
			console.error("Failed to load table config from database, using defaults", error);
			this.config = defaultTableConfig;
		}
	}

	updateConfig(partialConfig: Partial<TableConfiguration>): void {
		this.config = { ...this.config, ...partialConfig };
	}

	getConfig(): TableConfiguration {
		return this.config;
	}

	getColumnsConfig(): ColumnsConfig {
		return this.config.columns;
	}

	getRowsConfig(): RowsConfig {
		return this.config.rows;
	}

	getTypographyConfig(): TableTypographyConfig {
		return this.config.typography;
	}

	getBordersConfig(): BordersConfig {
		return this.config.borders;
	}

	getColorsConfig(): TableColorsConfig {
		return this.config.colors;
	}

	getAlignmentConfig(): AlignmentConfig {
		return this.config.alignment;
	}

	getFreezingConfig(): FreezingConfig {
		return this.config.freezing;
	}

	getSpacingConfig(): TableSpacingConfig {
		return this.config.spacing;
	}

	getStatMarkerConfig(): IStatMarkerConfig {
		return this.config.statMarkers;
	}

	/**
	 * Get attribute column min width based on chart type
	 */
	getAttributeMinWidth(isColumnChart: boolean = false): number {
		const attributeConfig = this.config.columns.attribute;
		return isColumnChart ? attributeConfig.minWidthColumnCharts : attributeConfig.minWidth;
	}

	/**
	 * Get attribute column min width based on chart type
	 */
	getNumericColumnMaxWidth(): number {
		const numericConfig = this.config.columns.numerical;
		return numericConfig.maxWidth;
	}

	/**
	 * Calculate column width within constraints
	 */
	calculateColumnWidth(columnType: "attribute" | "wave" | "numerical", providedWidth?: number): number {
		const columnConfig = this.config.columns[columnType];

		if (providedWidth !== undefined) {
			return Math.min(Math.max(providedWidth, columnConfig.minWidth), columnConfig.maxWidth);
		}

		return columnConfig.minWidth;
	}

	/**
	 * Get cell style based on column type
	 */
	getCellStyle(columnType: "attribute" | "wave" | "numerical", isHeader: boolean = false): React.CSSProperties {
		const typography = this.config.typography;
		const alignment = this.config.alignment;
		const spacing = this.config.spacing;
		const columnConfig = this.config.columns[columnType];

		return {
			fontSize: `${typography.fontSize.data}px`,
			fontWeight: isHeader ? typography.fontWeight.header : typography.fontWeight.data,
			color: isHeader ? typography.color.header : typography.color.default,
			textAlign: alignment.horizontal[columnType],
			verticalAlign: alignment.vertical.data,
			padding: `${spacing.padding.cell.vertical}px ${spacing.padding.cell.horizontal}px`,
			whiteSpace: columnConfig.wrapText ? "normal" : "nowrap",
			overflow: columnConfig.truncate ? "hidden" : "visible",
			textOverflow: columnConfig.truncate ? "ellipsis" : "clip",
		};
	}

	/**
	 * Get header style based on hierarchy level
	 */
	getHeaderStyle(level: number, isLeafLevel: boolean): React.CSSProperties {
		const hierarchy = this.config.typography.headerHierarchy;
		const fontWeight = "font-normal";

		if (isLeafLevel) {
			return {
				fontSize: `${hierarchy.leafLevel.fontSize}px`,
				fontWeight: fontWeight,
				color: hierarchy.leafLevel.color,
				lineHeight: `${hierarchy.leafLevel.lineHeight}px`,
			};
		}

		switch (level) {
			case 0:
				return {
					fontSize: `${hierarchy.level0.fontSize}px`,
					fontWeight: fontWeight,
					color: hierarchy.level0.color,
					lineHeight: `${hierarchy.level0.lineHeight}px`,
				};
			case 1:
				return {
					fontSize: `${hierarchy.level1.fontSize}px`,
					fontWeight: fontWeight,
					color: hierarchy.level1.color,
					lineHeight: `${hierarchy.level1.lineHeight}px`,
				};
			case 2:
				return {
					fontSize: `${hierarchy.level2.fontSize}px`,
					fontWeight: fontWeight,
					color: hierarchy.level2.color,
					lineHeight: `${hierarchy.level2.lineHeight}px`,
				};
			default:
				return {
					fontSize: `${hierarchy.default.fontSize}px`,
					fontWeight: fontWeight,
					color: hierarchy.default.color,
					lineHeight: `${hierarchy.default.lineHeight}px`,
				};
		}
	}

	/**
	 * Get n-size row style (separate from header hierarchy)
	 */
	getNSizeRowStyle(): React.CSSProperties {
		const hierarchy = this.config.typography.headerHierarchy;
		return {
			fontSize: `${hierarchy.nSizeRow.fontSize}px`,
			fontWeight: hierarchy.nSizeRow.fontWeight,
			color: hierarchy.nSizeRow.color,
			lineHeight: `${hierarchy.nSizeRow.lineHeight}px`,
		};
	}

	/**
	 * Check if n-size should be highlighted (n < 30)
	 */
	shouldHighlightNSize(nSize: number | string): boolean {
		if (typeof nSize === "string") return false;
		return nSize < 30;
	}

	/**
	 * Get n-size color
	 */
	getNSizeColor(nSize: number | string): string {
		return this.shouldHighlightNSize(nSize) ? this.config.typography.color.lowNSize : this.config.typography.color.tertiary;
	}

	/**
	 * Get width style object for column
	 */
	getWidthStyle(columnType: "attribute" | "wave" | "numerical", width?: number): React.CSSProperties {
		const columnConfig = this.config.columns[columnType];
		const calculatedWidth = this.calculateColumnWidth(columnType, width);

		return {
			minWidth: `${columnConfig.minWidth}px`,
			maxWidth: `${columnConfig.maxWidth}px`,
			width: `${calculatedWidth}px`,
		};
	}

	// Add getter to TableConfigManager class:
	getYAxisWidthConfig(): ITableYAxisWidthConfig {
		return this.config.yAxisWidth;
	}
}

/**
 * Hook for using table configuration in React components
 */
export const useTableConfig = () => {
	return TableConfigManager.getInstance().getConfig();
};

/**
 * Export singleton instance for direct access
 */
export const tableConfig = TableConfigManager.getInstance();
